
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { toast } from '@/hooks/use-toast';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Printer as PrinterIcon, RefreshCw, Settings2, Globe, Server } from 'lucide-react';
import { getPrinters, checkPrintService } from '@/services/printService';

interface Printer {
  name: string;
  isDefault: boolean;
  status: string;
}

const PrinterSettings: React.FC = () => {
  const [printers, setPrinters] = useState<Printer[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [manualPrinterName, setManualPrinterName] = useState('');
  const [selectedPrinter, setSelectedPrinter] = useState('');
  const [printServiceUrl, setPrintServiceUrl] = useState('http://localhost:3000');
  const [printServerIp, setPrintServerIp] = useState('');
  const [serviceAvailable, setServiceAvailable] = useState<boolean | null>(null);
  const [showPrinterDetails, setShowPrinterDetails] = useState(false);
  const [selectedPrinterDetails, setSelectedPrinterDetails] = useState<Printer | null>(null);

  useEffect(() => {
    const savedPrinter = localStorage.getItem('selected_printer');
    const savedUrl = localStorage.getItem('print_service_url');
    const savedIp = localStorage.getItem('print_server_ip');
    
    if (savedPrinter) setSelectedPrinter(savedPrinter);
    if (savedUrl) setPrintServiceUrl(savedUrl);
    if (savedIp) setPrintServerIp(savedIp);
    
    checkServiceAvailability();
  }, []);

  const checkServiceAvailability = async () => {
    try {
      const isAvailable = await checkPrintService();
      setServiceAvailable(isAvailable);
      
      if (isAvailable) {
        fetchPrinters();
      }
    } catch (error) {
      setServiceAvailable(false);
      console.error("Erro ao verificar serviço de impressão:", error);
    }
  };

  const fetchPrinters = async () => {
    setIsLoading(true);
    try {
      const availablePrinters = await getPrinters();
      setPrinters(availablePrinters);
      
      if (!selectedPrinter) {
        const defaultPrinter = availablePrinters.find(p => p.isDefault);
        if (defaultPrinter) {
          setSelectedPrinter(defaultPrinter.name);
        }
      }
      
      toast({
        title: "Impressoras encontradas",
        description: `Foram encontradas ${availablePrinters.length} impressoras disponíveis.`,
      });
    } catch (error) {
      console.error("Erro ao buscar impressoras:", error);
      toast({
        title: "Erro ao buscar impressoras",
        description: "Não foi possível obter a lista de impressoras. Verifique se o serviço está em execução.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectPrinter = (printerName: string) => {
    setSelectedPrinter(printerName);
    localStorage.setItem('selected_printer', printerName);
    
    toast({
      title: "Impressora selecionada",
      description: `A impressora "${printerName}" foi configurada como padrão.`,
    });
  };

  const handleSaveManualPrinter = () => {
    if (!manualPrinterName.trim()) {
      toast({
        title: "Nome inválido",
        description: "Por favor, digite um nome de impressora válido.",
        variant: "destructive",
      });
      return;
    }
    
    handleSelectPrinter(manualPrinterName.trim());
    setManualPrinterName('');
  };

  const handleSaveServiceUrl = () => {
    try {
      if (!printServiceUrl.startsWith('http://') && !printServiceUrl.startsWith('https://')) {
        throw new Error("URL deve começar com http:// ou https://");
      }
      
      localStorage.setItem('print_service_url', printServiceUrl);
      
      toast({
        title: "URL do serviço salva",
        description: "A URL do serviço de impressão foi salva com sucesso.",
      });
      
      checkServiceAvailability();
    } catch (error) {
      toast({
        title: "URL inválida",
        description: error instanceof Error ? error.message : "Formato de URL inválido",
        variant: "destructive",
      });
    }
  };

  const handleSavePrintServerIp = () => {
    try {
      // Validar o IP básico
      if (printServerIp && !/^(\d{1,3}\.){3}\d{1,3}(:\d+)?$/.test(printServerIp)) {
        throw new Error("Formato de IP inválido. Use o formato 192.168.1.10 ou 192.168.1.10:3000");
      }
      
      // Se vazio, remover a configuração
      if (!printServerIp.trim()) {
        localStorage.removeItem('print_server_ip');
        toast({
          title: "Configuração de IP removida",
          description: "O servidor de impressão voltará a usar o modo local (localhost).",
        });
        return;
      }
      
      // Salvar a configuração
      localStorage.setItem('print_server_ip', printServerIp);
      
      // Se o IP não inclui porta, adicionar a porta padrão 3000
      let fullAddress = printServerIp;
      if (!printServerIp.includes(':')) {
        fullAddress = `${printServerIp}:3000`;
      }
      
      // Atualizar também a URL do serviço para usar este IP
      const newServiceUrl = `http://${fullAddress}`;
      localStorage.setItem('print_service_url', newServiceUrl);
      setPrintServiceUrl(newServiceUrl);
      
      toast({
        title: "Servidor de impressão configurado",
        description: `O servidor de impressão foi configurado para ${newServiceUrl}`,
      });
      
      // Verificar disponibilidade com o novo endereço
      setTimeout(checkServiceAvailability, 500);
    } catch (error) {
      toast({
        title: "IP inválido",
        description: error instanceof Error ? error.message : "Formato de IP inválido",
        variant: "destructive",
      });
    }
  };
  
  const showPrinterInfo = (printer: Printer) => {
    setSelectedPrinterDetails(printer);
    setShowPrinterDetails(true);
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <PrinterIcon className="h-5 w-5" />
          Configurações de Impressão
        </CardTitle>
        <CardDescription>Configure impressoras e serviços de impressão</CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="service">
          <TabsList className="grid grid-cols-2 mb-4">
            <TabsTrigger value="service" className="flex items-center gap-1">
              <Settings2 className="h-4 w-4" />
              Serviço de Impressão
            </TabsTrigger>
            <TabsTrigger value="printers" className="flex items-center gap-1">
              <PrinterIcon className="h-4 w-4" />
              Impressoras
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="service" className="space-y-4">
            <div className="space-y-4 border p-4 rounded-md">
              <h3 className="font-medium">Configuração de Servidor de Impressão</h3>
              
              <div className="space-y-2">
                <Label htmlFor="print-server-ip" className="flex items-center gap-1">
                  <Server className="h-4 w-4" />
                  IP do Servidor de Impressão
                </Label>
                <div className="flex space-x-2">
                  <Input
                    id="print-server-ip"
                    placeholder="192.168.1.10"
                    value={printServerIp}
                    onChange={(e) => setPrintServerIp(e.target.value)}
                  />
                  <Button onClick={handleSavePrintServerIp}>Salvar</Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Digite o IP do computador onde o serviço de impressão está instalado. Deixe em branco para usar o modo local (localhost).
                </p>
                <p className="text-xs text-muted-foreground">
                  Exemplo: 192.168.1.10 ou 192.168.1.10:3000 (se a porta for diferente da padrão)
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="print-service-url">URL do Serviço de Impressão</Label>
              <div className="flex space-x-2">
                <Input
                  id="print-service-url"
                  placeholder="http://localhost:3000"
                  value={printServiceUrl}
                  onChange={(e) => setPrintServiceUrl(e.target.value)}
                />
                <Button onClick={handleSaveServiceUrl}>Salvar</Button>
              </div>
              <p className="text-xs text-muted-foreground">
                URL base do serviço de impressão sem barras no final (ex: http://localhost:3000)
              </p>
              
              {serviceAvailable !== null && (
                <div className={`mt-3 p-3 rounded-md text-sm ${serviceAvailable ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                  {serviceAvailable ? (
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span>Serviço de impressão conectado e disponível</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                      <span>Serviço de impressão não disponível</span>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <div className="bg-blue-50 p-4 rounded-md border border-blue-200 mt-6">
              <h3 className="text-sm font-medium text-blue-800 mb-2">Informações sobre o Serviço de Impressão</h3>
              <div className="text-xs text-blue-700 space-y-2">
                <p>
                  O serviço de impressão local deve estar em execução no endereço configurado acima.
                  Por padrão, o serviço usa a porta 3000 (http://localhost:3000).
                </p>
                <p>
                  Para imprimir a partir de outros dispositivos na mesma rede, configure o IP do servidor onde o serviço está instalado.
                </p>
                <p>
                  Para imprimir relatórios, o serviço deve estar disponível e uma impressora deve estar configurada.
                </p>
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="printers" className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Impressoras Disponíveis</Label>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={fetchPrinters} 
                  disabled={isLoading}
                  className="flex items-center gap-1"
                >
                  {isLoading ? (
                    <>
                      <RefreshCw className="h-4 w-4 animate-spin" />
                      <span>Buscando...</span>
                    </>
                  ) : (
                    <>
                      <RefreshCw className="h-4 w-4" />
                      <span>Buscar Impressoras</span>
                    </>
                  )}
                </Button>
              </div>
              
              {printers.length > 0 ? (
                <div className="grid gap-2 max-h-64 overflow-y-auto">
                  {printers.map((printer) => (
                    <div 
                      key={printer.name}
                      className={`p-2 border rounded-md cursor-pointer hover:bg-slate-50 flex justify-between items-center ${
                        selectedPrinter === printer.name ? 'bg-blue-50 border-blue-300' : 'border-slate-200'
                      }`}
                      onClick={() => handleSelectPrinter(printer.name)}
                    >
                      <div className="flex items-center gap-2">
                        <PrinterIcon className="h-4 w-4 text-slate-500" />
                        <div>
                          <div className="font-medium">{printer.name}</div>
                          <div className="text-xs text-slate-500 flex items-center gap-1">
                            <span className={`w-2 h-2 rounded-full ${printer.status === 'Ready' ? 'bg-green-500' : 'bg-amber-500'}`}></span>
                            <span>{printer.status}</span>
                            {printer.isDefault && <span className="text-blue-600">(Padrão)</span>}
                          </div>
                        </div>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-8 w-8 p-0" 
                        onClick={(e) => {
                          e.stopPropagation();
                          showPrinterInfo(printer);
                        }}
                      >
                        <Settings2 className="h-4 w-4" />
                        <span className="sr-only">Detalhes</span>
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center p-4 border border-dashed rounded-md">
                  {serviceAvailable === false ? (
                    <p className="text-slate-500">Serviço de impressão não disponível</p>
                  ) : (
                    <p className="text-slate-500">Nenhuma impressora encontrada</p>
                  )}
                </div>
              )}
            </div>
            
            <div className="space-y-2 pt-4 border-t">
              <Label htmlFor="manual-printer">Nome da Impressora Manual</Label>
              <div className="flex space-x-2">
                <Input
                  id="manual-printer"
                  placeholder="Digite o nome exato da impressora"
                  value={manualPrinterName}
                  onChange={(e) => setManualPrinterName(e.target.value)}
                />
                <Button onClick={handleSaveManualPrinter}>Definir</Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Use esta opção se você conhece o nome exato da impressora e deseja configurá-la manualmente.
              </p>
            </div>
            
            {selectedPrinter && (
              <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-md">
                <p className="text-sm text-green-700">
                  <span className="font-medium">Impressora atual:</span> {selectedPrinter}
                </p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
      
      <Dialog open={showPrinterDetails} onOpenChange={setShowPrinterDetails}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Detalhes da Impressora</DialogTitle>
            <DialogDescription>
              Informações sobre a impressora selecionada
            </DialogDescription>
          </DialogHeader>
          
          {selectedPrinterDetails && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <p className="text-sm font-medium text-muted-foreground">Nome</p>
                  <p>{selectedPrinterDetails.name}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-medium text-muted-foreground">Status</p>
                  <p>{selectedPrinterDetails.status}</p>
                </div>
              </div>
              
              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">Impressora Padrão</p>
                <p>{selectedPrinterDetails.isDefault ? 'Sim' : 'Não'}</p>
              </div>
              
              <Button 
                className="w-full" 
                onClick={() => {
                  handleSelectPrinter(selectedPrinterDetails.name);
                  setShowPrinterDetails(false);
                }}
              >
                Selecionar como Impressora Padrão
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default PrinterSettings;
